import type {FrontLinks, WpmlLinks, YoastHead} from "@/generated/api";
import {relativeLinkOf} from "@/utils/index";
import {getTranslatedUrl} from "./getLanguageFromDomain";
import {getRobotsContent, mapSchemaStrings} from "./schema";
import {formatAndLimit, stripHtmlTags} from "./text";

// German pages redirect to the English site, so they are never declared as alternates
const HREFLANG_LANGUAGES = ["fr", "en", "es", "it"];

// What the meta tags are built from: the fields every content type carries (the contract: Post, Page, Destination), and the text of its ACF
// (`acf` is an object, or `[false]` / `[]` when the content has none)
export interface MetaPage {
  link?: string;
  front_links?: FrontLinks;
  wpml_links?: WpmlLinks;
  yoast_head_json?: Partial<YoastHead>;
  acf?: unknown;
}

// The handful of shapes getMeta/getPreviewMeta build locally (Remix meta descriptors)
interface MetaTag {
  title?: string;
  name?: string;
  property?: string;
  content?: string | null;
  tagName?: string;
  rel?: string;
  hrefLang?: string;
  href?: string | null;
}

const getAlternateLinks = (
  wpmlLinks: Record<string, unknown> | undefined,
  frontOrigin: string | undefined,
) => {
  if (!wpmlLinks || !frontOrigin) {
    return [];
  }
  const domain = new URL(frontOrigin).host;
  const links = HREFLANG_LANGUAGES.map((lang) => ({
    tagName: "link",
    rel: "alternate",
    hrefLang: lang,
    href: getTranslatedUrl(domain, lang, wpmlLinks[lang]),
  })).filter(({href}) => href);
  return links.length > 1 ? links : [];
};

/**
 * Yoast builds its URLs from the WordPress install: admin domain, WPML "?lang=xx"
 * and the untranslated "/article/" base. Maps them to the public magazine URL:
 * the page's own permalink becomes its front URL (front_links), other admin URLs
 * get the front origin; media stay served by WordPress.
 */
const toFrontUrl = (
  page: MetaPage | null | undefined,
  adminOrigin?: string,
  frontOrigin?: string,
) => {
  const permalink = page?.link;
  const path = relativeLinkOf(page?.front_links);
  // Anything that is not a string (a missing canonical, a number of a schema) goes through as it came
  return <T>(value: T): T | string => {
    if (
      !adminOrigin ||
      !frontOrigin ||
      typeof value !== "string" ||
      !value.startsWith(adminOrigin) ||
      value.startsWith(`${adminOrigin}/wp-content/`)
    ) {
      return value;
    }
    const rest =
      permalink && value.startsWith(permalink) && value.slice(permalink.length);
    if (path != null && rest !== false && /^($|[#?])/.test(String(rest))) {
      return `${frontOrigin}${path}${rest}`;
    }
    return (
      frontOrigin +
      value.slice(adminOrigin.length).replace(/[?&]lang=[a-z]{2}(?=$|[#?&])/, "")
    );
  };
};

/**
 * @param page WordPress object (post, page, destination, homepage)
 * @param alternates WPML links by language, defaults to the page's own
 */
export const getMeta = ({
  page,
  adminOrigin,
  frontOrigin,
  alternates = page?.wpml_links,
}: {
  page: MetaPage | null | undefined;
  adminOrigin?: string;
  frontOrigin?: string;
  alternates?: Record<string, unknown>;
}) => {
  const yoast: Partial<YoastHead> = page?.yoast_head_json || {};
  const toFront = toFrontUrl(page, adminOrigin, frontOrigin);
  const path = relativeLinkOf(page?.front_links);
  const pageUrl = frontOrigin && path != null ? `${frontOrigin}${path}` : yoast.og_url;
  const title = yoast.title || "Le magazine ViaMichelin";
  const description =
    yoast.description ||
    formatAndLimit(stripHtmlTags((page?.acf as {text?: string} | undefined)?.text));

  const meta: MetaTag[] = [
    {
      name: "description",
      content: description,
    },
    {
      property: "og:description",
      content: description,
    },
    {property: "og:image", content: yoast.og_image?.[0]?.url},

    {property: "og:locale", content: yoast.og_locale},
    {property: "og:site_name", content: yoast.og_site_name},
    {property: "og:title", content: yoast.og_title},
    {property: "og:url", content: pageUrl},
    {property: "og:type", content: yoast.og_type},
    {name: "revised", content: yoast.article_modified_time},
    {name: "robots", content: getRobotsContent(yoast.robots)},
    // Yoast omits the canonical on noindex sites (local, staging): fall back to the page URL
    {tagName: "link", rel: "canonical", href: toFront(yoast.canonical) || pageUrl},
    ...getAlternateLinks(alternates, frontOrigin),
  ];
  const filteredMeta: MetaTag[] = meta.filter((obj) => !!obj.content || !!obj.href);
  filteredMeta.unshift({title});
  return filteredMeta;
};

// A preview is neither canonical nor indexable, whatever Yoast says about the published post
export const getPreviewMeta = (args: Parameters<typeof getMeta>[0]) => [
  ...getMeta(args).filter(
    (m: MetaTag) =>
      m.rel !== "canonical" && m.property !== "og:url" && m.name !== "robots",
  ),
  {name: "robots", content: "noindex, nofollow"},
];

export const getSchema = (
  page: MetaPage | null | undefined,
  adminOrigin?: string,
  frontOrigin?: string,
) =>
  mapSchemaStrings(
    page?.yoast_head_json?.schema,
    toFrontUrl(page, adminOrigin, frontOrigin) as (s: string) => string,
  );
